import { ComponentPropsWithoutRef, forwardRef, Ref, useContext } from "react";
import { createPortal } from "react-dom";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { LayoutUtilProps } from "../../types";
import cx from "classnames";
import styles from "./Page.module.scss";
import { PageContext } from "./PageContext";

/**
 * Props for the PageFooter component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type PageFooterProps = ComponentPropsWithoutRef<"div"> & LayoutUtilProps;

/**
 * PageFooter component for creating page footers with layout utilities.
 *
 * Features:
 * - Semantic footer element
 * - Layout utility props support
 * - Responsive design
 * - Accessibility support with proper footer landmark
 * - Flexible content positioning
 * - Portals into Page's footer slot via context
 *
 * @example
 * <Page.Footer>
 *   <Text>Footer content with layout utilities</Text>
 * </Page.Footer>
 */
export const PageFooter = forwardRef(
  (props: PageFooterProps, ref: Ref<HTMLDivElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const { children, className, style, ...rest } = componentProps;
    const { footerSlot } = useContext(PageContext);

    const classNames = cx([styles["content-footer"]], className);
    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const footer = (
      <footer className={classNames} ref={ref} style={styleCombined} {...rest}>
        {children}
      </footer>
    );

    if (footerSlot) {
      return createPortal(footer, footerSlot);
    }

    if (footerSlot === undefined) {
      return footer;
    }

    return null;
  },
);
PageFooter.displayName = "PageFooter";
